import type { OutboxStore } from '../db/indexeddb.ts';
import type { OutboxRecord } from '../db/outbox-record.ts';
import type { SendResult } from './outbox.ts';

/**
 * G-003 — the retry engine (§10).
 *
 * Background Sync is deliberately not the mechanism: it is unavailable on
 * iOS Safari, which is most of a Vietnamese running event. Recovery is driven
 * by events the page can actually observe — coming online, becoming visible,
 * starting up, a manual tap, and a slow poll while the page stays open.
 */

/** §10 — bounded, never faster than 2s, never slower than a minute. */
export const BACKOFF_SECONDS = [2, 5, 10, 20, 30, 60] as const;

export function backoffDelayMs(attemptCount: number): number {
  if (attemptCount <= 0) return 0;
  const index = Math.min(attemptCount, BACKOFF_SECONDS.length) - 1;
  return (BACKOFF_SECONDS[index] ?? 60) * 1000;
}

/**
 * The only two fields the backoff schedule reads.
 *
 * Structural on purpose: check-outs (G-011/G-012) are a second record shape
 * that must be paced by *this* schedule and no other, so they satisfy this
 * instead of copying the timing rules into a second module. A device that ran
 * two different retry rhythms would defeat the point of §10 — every phone in
 * the venue backing off together.
 */
export interface BackoffSchedulable {
  attemptCount: number;
  lastAttemptAt?: string;
}

/** When a record earned its next attempt, given the backoff schedule. */
export function nextAttemptAt(record: BackoffSchedulable): number {
  if (!record.lastAttemptAt) return 0;
  const last = Date.parse(record.lastAttemptAt);
  if (Number.isNaN(last)) return 0;
  return last + backoffDelayMs(record.attemptCount);
}

export interface RetryEngineDeps {
  outbox: OutboxStore;
  send: (record: OutboxRecord) => Promise<SendResult>;
  /** Only records for the event currently on screen are retried. */
  eventId?: string;
  now?: () => number;
  isOnline?: () => boolean;
  isVisible?: () => boolean;
  /** Called after every attempt so the UI can follow the state machine. */
  onResult?: (result: SendResult) => void;
  onQueueChange?: (pending: OutboxRecord[]) => void;
  pollIntervalMs?: number;
  /**
   * Where the `online` and `visibilitychange` events come from. Injected so a
   * test can dispatch them, and so this module never assumes a `window`.
   */
  onlineTarget?: EventTarget;
  visibilityTarget?: EventTarget;
}

/**
 * Slow on purpose: the event-driven triggers do the real work, and a page left
 * open on a lock screen should not wake up every second.
 */
const DEFAULT_POLL_INTERVAL_MS = 15_000;

export class RetryEngine {
  readonly #deps: RetryEngineDeps;
  readonly #pollIntervalMs: number;
  readonly #now: () => number;
  readonly #isOnline: () => boolean;
  readonly #isVisible: () => boolean;
  readonly #teardown: (() => void)[] = [];

  #timer: ReturnType<typeof setInterval> | null = null;
  #running = false;
  #started = false;
  /** Honours a server `Retry-After` across the whole outbox, not one record. */
  #pauseUntil = 0;

  constructor(deps: RetryEngineDeps) {
    this.#deps = deps;
    this.#pollIntervalMs = deps.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    this.#now = deps.now ?? (() => Date.now());
    this.#isOnline = deps.isOnline ?? (() => navigator.onLine);
    this.#isVisible = deps.isVisible ?? (() => document.visibilityState === 'visible');
  }

  /** Startup trigger plus the listeners for the other three. */
  start(): void {
    if (this.#started) return;
    this.#started = true;

    const online = () => {
      void this.drain();
    };
    const visibility = () => {
      if (this.#isVisible()) {
        this.#startPolling();
        void this.drain();
      } else {
        // "app hidden pauses unnecessary periodic retries" — the event-driven
        // triggers still fire, only the poll stops.
        this.#stopPolling();
      }
    };

    const onlineTarget = this.#deps.onlineTarget ?? globalThis;
    const visibilityTarget = this.#deps.visibilityTarget ?? globalThis.document;

    onlineTarget.addEventListener('online', online);
    visibilityTarget.addEventListener('visibilitychange', visibility);
    this.#teardown.push(
      () => {
        onlineTarget.removeEventListener('online', online);
      },
      () => {
        visibilityTarget.removeEventListener('visibilitychange', visibility);
      },
    );

    if (this.#isVisible()) this.#startPolling();
    void this.drain();
  }

  stop(): void {
    this.#stopPolling();
    for (const undo of this.#teardown.splice(0)) undo();
    this.#started = false;
  }

  #startPolling(): void {
    this.#timer ??= setInterval(() => {
      void this.drain();
    }, this.#pollIntervalMs);
  }

  #stopPolling(): void {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  /** Manual "GỬI LẠI NGAY" (§15): ignores backoff, still one at a time. */
  async retryNow(): Promise<void> {
    this.#pauseUntil = 0;
    await this.drain({ force: true });
  }

  /**
   * One pass over the outbox.
   *
   * Sequential by design (§19): parallel sends would fire several Turnstile
   * challenges at once and, on a venue network, compete for the same few
   * kilobits. Re-entrancy is refused rather than queued — the next trigger
   * will pick up whatever this pass leaves behind.
   */
  async drain(options: { force?: boolean } = {}): Promise<void> {
    if (this.#running) return;
    if (!this.#isOnline()) return;
    if (!options.force && this.#now() < this.#pauseUntil) return;

    this.#running = true;
    try {
      const pending = await this.#deps.outbox.listPending(this.#deps.eventId);
      this.#deps.onQueueChange?.(pending);

      for (const record of pending) {
        if (!this.#isOnline()) break;
        if (!options.force && this.#now() < nextAttemptAt(record)) continue;

        const result = await this.#deps.send(record);
        this.#deps.onResult?.(result);

        if (result.kind === 'pending' && result.retryAfterSeconds !== undefined) {
          this.#pauseUntil = this.#now() + result.retryAfterSeconds * 1000;
          break;
        }

        // A failed challenge stops the pass: §16 forbids generating challenges
        // back to back, and the participant is the one who has to solve it.
        if (result.kind === 'verify_retry') break;
      }

      this.#deps.onQueueChange?.(await this.#deps.outbox.listPending(this.#deps.eventId));
    } finally {
      this.#running = false;
    }
  }
}
